import React, { useState, useEffect } from 'react';
import { 
  Users, 
  ShieldAlert, 
  QrCode, 
  Zap, 
  Phone, 
  Printer, 
  Trash2, 
  Edit, 
  CheckCircle2, 
  AlertTriangle,
  ShieldCheck
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { WorkerList } from '../../../components/hr/WorkerList';
import { WorkerModal } from '../../../components/hr/WorkerModal';

export const HRModule: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'workers' | 'alerts' | 'badges'>('workers');
  
  // Data state
  const [workers, setWorkers] = useState<any[]>([]);
  const [alerts, setAlerts] = useState<any>(null);

  // Badge state
  const [selectedBadgeWorkerId, setSelectedBadgeWorkerId] = useState<string>('');
  const [badgeData, setBadgeData] = useState<any | null>(null);
  const [badgeLoading, setBadgeLoading] = useState(false);

  // Fetch Workers for counts & badges
  const fetchWorkers = async () => {
    try {
      const res = await fetch('/api/v1/hr/workers');
      if (res.ok) {
        const json = await res.json();
        if (json.success) {
          setWorkers(json.data);
          if (!selectedBadgeWorkerId && json.data.length > 0) {
            setSelectedBadgeWorkerId(json.data[0].id);
          }
        }
      }
    } catch (err) {
      console.error('Failed to fetch workers:', err);
    }
  };

  // Fetch Alerts
  const fetchAlerts = async () => {
    try {
      const res = await fetch('/api/v1/hr/alerts');
      if (res.ok) {
        const json = await res.json();
        if (json.success) setAlerts(json.data);
      }
    } catch (err) {
      console.error('Failed to fetch alerts:', err);
    }
  };

  // Fetch Badge Info for Selected Worker
  const fetchBadge = async (workerId: string) => {
    if (!workerId) return;
    setBadgeLoading(true);
    try {
      const res = await fetch(`/api/v1/hr/workers/${workerId}/badge`);
      if (res.ok) {
        const json = await res.json();
        if (json.success) setBadgeData(json.data);
      }
    } catch (err) {
      console.error('Failed to fetch badge:', err);
    } finally {
      setBadgeLoading(false);
    }
  };

  useEffect(() => {
    fetchWorkers();
    fetchAlerts();
  }, []);

  useEffect(() => {
    if (activeTab === 'badges' && selectedBadgeWorkerId) {
      fetchBadge(selectedBadgeWorkerId);
    }
  }, [activeTab, selectedBadgeWorkerId]);

  return (
    <div className="space-y-6 w-full">
      {/* Tab Navigation */}
      <div className="flex bg-white p-1.5 rounded-2xl border border-slate-200/90 max-w-xl mx-auto shadow-sm gap-1.5">
        <button
          type="button"
          onClick={() => setActiveTab('workers')}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl text-xs sm:text-sm font-semibold transition active:scale-95 ${
            activeTab === 'workers'
              ? 'bg-blue-600 text-white shadow-md shadow-blue-600/20 font-extrabold'
              : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
          }`}
        >
          <Users className="w-4 h-4" />
          <span>Картотека ({workers.length})</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('alerts')}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl text-xs sm:text-sm font-semibold transition relative active:scale-95 ${
            activeTab === 'alerts'
              ? 'bg-amber-500 text-slate-950 shadow-md shadow-amber-500/20 font-extrabold'
              : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
          }`}
        >
          <ShieldAlert className="w-4 h-4" />
          <span>Алерты Патентов & ЭБ</span>
          {alerts && (alerts.expiredRostechnadzor?.length > 0 || alerts.expiringRostechnadzor?.length > 0) && (
            <span className="w-2.5 h-2.5 rounded-full bg-rose-500 animate-ping absolute top-2 right-2" />
          )}
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('badges')}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl text-xs sm:text-sm font-semibold transition active:scale-95 ${
            activeTab === 'badges'
              ? 'bg-emerald-600 text-white shadow-md shadow-emerald-600/20 font-extrabold'
              : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
          }`}
        >
          <QrCode className="w-4 h-4" />
          <span>QR-бейджи</span>
        </button>
      </div>

      {/* TAB 1: WORKERS CATALOG TABLE (Full-Width Spacious Table View) */}
      {activeTab === 'workers' && (
        <WorkerList onWorkersUpdated={fetchWorkers} />
      )}

      {/* TAB 2: SAFETY & DOCUMENT ALERTS */}
      {activeTab === 'alerts' && (
        <div className="space-y-6 max-w-4xl mx-auto">
          {/* Summary Dashboard Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="bg-rose-50 border border-rose-200 p-4 rounded-2xl space-y-1 shadow-sm">
              <span className="text-xs font-bold text-rose-800 uppercase tracking-wider block">Просрочен ЭБ / Документы</span>
              <div className="text-2xl font-black text-rose-900">
                {alerts?.expiredRostechnadzor?.length || 0} чел.
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 p-4 rounded-2xl space-y-1 shadow-sm">
              <span className="text-xs font-bold text-amber-800 uppercase tracking-wider block">Истекает в течение 30 дней</span>
              <div className="text-2xl font-black text-amber-900">
                {alerts?.expiringRostechnadzor?.length || 0} чел.
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 p-4 rounded-2xl space-y-1 shadow-sm">
              <span className="text-xs font-bold text-blue-800 uppercase tracking-wider block">Высота (Приказ 782н)</span>
              <div className="text-2xl font-black text-blue-900">
                {alerts?.expiredHeightPermits?.length || 0} чел.
              </div>
            </div>

            <div className="bg-white border border-slate-200 p-4 rounded-2xl space-y-1 shadow-sm">
              <span className="text-xs font-bold text-slate-500 uppercase tracking-wider block">Истекшие патенты</span>
              <div className="text-2xl font-black text-slate-900">
                {alerts?.expiredPatents?.length || 0} чел.
              </div>
            </div>
          </div>

          {/* Expired Rostechnadzor Detailed Alert List */}
          <div className="bg-white border border-slate-200/90 rounded-2xl p-5 space-y-4 shadow-sm">
            <h3 className="text-base font-bold text-rose-800 flex items-center gap-2 border-b border-slate-100 pb-3">
              <ShieldAlert className="w-5 h-5 text-rose-600" />
              <span>Требуют немедленного отстранения или переаттестации:</span>
            </h3>

            {(!alerts || alerts.expiredRostechnadzor?.length === 0) ? (
              <div className="p-4 text-center text-slate-500 text-xs flex items-center justify-center gap-2 bg-emerald-50 rounded-xl border border-emerald-200">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                <span className="font-semibold text-emerald-800">Все документы и аттестации сотрудников действительны!</span>
              </div>
            ) : (
              <div className="space-y-3">
                {alerts.expiredRostechnadzor.map((w: any) => (
                  <div key={w.id} className="bg-rose-50/80 border border-rose-200 p-4 rounded-xl flex items-center justify-between gap-3 shadow-sm">
                    <div>
                      <h4 className="font-bold text-rose-950 text-sm">{w.fullName}</h4>
                      <p className="text-xs text-slate-600">{w.position || w.specialty} • Группа ЭБ: {w.ebGroup}</p>
                    </div>
                    <div className="text-right">
                      <span className="text-xs font-bold text-rose-700 block">Просрочено: {w.rostechnadzorDate || w.ebNextExamDate}</span>
                      <span className="text-[10px] text-rose-600 font-bold">Охрана труда: Запрет допуска</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* TAB 3: ELECTRONIC QR BADGE CARD GENERATOR */}
      {activeTab === 'badges' && (
        <div className="space-y-6 max-w-2xl mx-auto">
          {/* Worker Selector */}
          <div className="bg-white p-4 rounded-2xl border border-slate-200/90 flex items-center justify-between gap-3 shadow-sm">
            <label className="text-xs font-bold text-slate-700 uppercase tracking-wider whitespace-nowrap">
              Выберите электромонтажника:
            </label>
            <select
              value={selectedBadgeWorkerId}
              onChange={(e) => setSelectedBadgeWorkerId(e.target.value)}
              className="flex-1 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs text-amber-800 font-bold focus:outline-none focus:border-amber-500"
            >
              {workers.map(w => (
                <option key={w.id} value={w.id}>
                  {w.fullName}
                </option>
              ))}
            </select>
          </div>

          {/* Badge Display Card */}
          {badgeLoading ? (
            <div className="p-8 text-center text-slate-400 text-sm">Генерация электронного бейджа...</div>
          ) : badgeData ? (
            <div className="bg-white border-2 border-amber-300 rounded-3xl p-6 shadow-xl space-y-6 text-center max-w-md mx-auto relative overflow-hidden">
              {/* Top Banner */}
              <div className="bg-gradient-to-r from-amber-500 to-amber-600 -mx-6 -mt-6 p-4 text-slate-950 flex items-center justify-center gap-2">
                <Zap className="w-5 h-5 fill-current" />
                <span className="font-black text-sm uppercase tracking-widest">
                  ЖК «Северная Звезда» • Пропуск ЭБ
                </span>
              </div>

              {/* Worker Photo & Main Info */}
              <div className="space-y-2 pt-2">
                <div className="w-24 h-24 rounded-2xl bg-slate-100 border-2 border-amber-300 mx-auto flex items-center justify-center overflow-hidden shadow-inner font-bold text-lg text-slate-700">
                  {badgeData.worker.photoUrl && badgeData.worker.photoUrl.startsWith('/') ? (
                    <img src={badgeData.worker.photoUrl} alt={badgeData.worker.fullName} className="w-full h-full object-cover" />
                  ) : (
                    <span>{badgeData.worker.fullName.split(' ').map((n: string) => n[0]).join('').slice(0, 2)}</span>
                  )}
                </div>

                <h2 className="text-lg font-black text-slate-900">{badgeData.worker.fullName}</h2>
                <p className="text-xs text-amber-800 font-bold">{badgeData.worker.position || badgeData.worker.specialty}</p>
              </div>

              {/* Validity Status Badge */}
              <div className={`p-3 rounded-xl border text-xs font-black uppercase tracking-wider flex items-center justify-center gap-2 ${
                badgeData.isValid
                  ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                  : 'bg-rose-50 border-rose-200 text-rose-800'
              }`}>
                {badgeData.isValid ? (
                  <>
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    <span>ДЕЙСТВИТЕЛЕН — ДОПУЩЕН К РАБОТЕ</span>
                  </>
                ) : (
                  <>
                    <AlertTriangle className="w-4 h-4 text-rose-600" />
                    <span>ЗАБЛОКИРОВАН (ПРОСРОЧЕН ЭБ/ПАТЕНТ)</span>
                  </>
                )}
              </div>

              {/* QR Code SVG Renderer */}
              <div className="bg-white p-4 rounded-2xl inline-block shadow-sm mx-auto border-2 border-slate-200">
                <QRCodeSVG value={badgeData.qrData} size={150} level="H" includeMargin={true} />
              </div>

              {/* Print Action */}
              <button
                onClick={() => window.print()}
                className="w-full py-3 bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold rounded-2xl shadow-md shadow-amber-500/20 transition flex items-center justify-center gap-2 text-xs active:scale-95"
              >
                <Printer className="w-4 h-4" />
                <span>Распечатать пропуск / бейдж</span>
              </button>
            </div>
          ) : (
            <div className="p-8 text-center text-slate-400 text-sm">Выберите сотрудника из списка</div>
          )}
        </div>
      )}
    </div>
  );
};

export default HRModule;
